import { NavigateFunction } from 'react-router';
import { IUser } from 'src/@types/user';
import { WorkspaceParticipant } from 'src/@types/workspace-info';
import { PATH_AFTER_LOGIN, PATH_AUTH } from 'src/Router/paths';
import { LoginResponse, UserRequest } from 'src/requests/user';

import { API_HOST } from 'src/config-global';
import {
  appLocationState,
  getSessionByUserId,
  getSessionSlug,
  startSessionFromLogin,
} from '../state';

export function getRouterBasedOnUserStats({
  workspaceInvite,
}: Partial<Pick<DoLoginProps, 'workspaceInvite'>> = {}): string {
  if (workspaceInvite)
    return `${PATH_AFTER_LOGIN}?workspaceInvite=${workspaceInvite}`;

  return PATH_AFTER_LOGIN;
}

type DoLoginProps = {
  email: string;
  password: string;
  token: string;
  method: 'token_info' | 'user_info';
  signup: boolean;
  redirect?: boolean;
  twoFactorCode?: string;
  workspaceInvite?: string;
  successCallback?: (user: IUser) => void | Promise<void>;
  navigate: NavigateFunction;
};
export async function doLogin({
  email,
  password,
  twoFactorCode,
  successCallback,
  workspaceInvite,
  navigate,
}: Omit<DoLoginProps, 'token' | 'method' | 'signup'>) {
  const loginResponse = await UserRequest.login({
    email,
    password,
    twoFactorCode,
  });

  postLogin(loginResponse, {
    email,
    password,
    navigate,
    successCallback,
    workspaceInvite,
  });
}

export async function doOAuthLogin({
  method,
  signup,
  token,
  twoFactorCode,
  successCallback,
  workspaceInvite,
  redirect,
  navigate,
}: Omit<DoLoginProps, 'email' | 'password'>) {
  const loginResponse = await UserRequest.oauthLogin({
    method,
    signup,
    token,
    twoFactorCode,
  });

  await postLogin(loginResponse, {
    method,
    signup,
    token,
    navigate,
    successCallback,
    workspaceInvite,
    redirect,
  });
}

export async function postLogin(
  { accessTokens, user, twoFactorEnabled, accountToken }: LoginResponse,
  {
    email,
    password,

    method,
    signup,
    token,

    redirect,
    navigate,
    successCallback,
    workspaceInvite,
  }: Partial<DoLoginProps>,
) {
  if (twoFactorEnabled) {
    navigate?.(PATH_AUTH.twoFactor, {
      state: {
        email,
        password,

        method,
        signup,
        token,
      },
    });
  } else {
    startSessionFromLogin({ user, accessTokens, accountToken });

    await successCallback?.(user);

    if (!redirect) return;

    //? just login and verify account uses this workflow today;
    //? after e2e tests coverage we will remove the function on this file

    const session = getSessionByUserId(user._id);

    //? when user had not join/create any workspace
    //? navigate to the next page to handle the workspace joining/creation
    if (!session) {
      navigate?.(PATH_AFTER_LOGIN);
      return;
    }
    const slug = session ? `/${getSessionSlug(session)}` : '';

    const nextLocation = workspaceInvite
      ? `${slug}${PATH_AFTER_LOGIN}?workspaceInvite=${workspaceInvite}`
      : `${slug}${PATH_AFTER_LOGIN}`;

    window.location.pathname = nextLocation;
  }
}

export function openAppAsUser(userId: string, replace?: boolean) {
  const { locations } = appLocationState.getState();
  const session = locations[userId];

  const workspaceSlug = getSessionSlug(session);
  const accountUrl = `${window.location.origin}/${workspaceSlug}/signed-in`;

  if (replace) {
    window.location.replace(accountUrl);
  } else {
    window.open(accountUrl, '_blank');
  }
}

export function getCover(shortName: string): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const firstLetterIndex = alphabet.indexOf(shortName[0].toUpperCase());
  const secondLetterIndex = alphabet.indexOf(shortName[1].toUpperCase());
  const thirdLetterIndex = alphabet.indexOf(shortName[2].toUpperCase());

  const baseNumber =
    firstLetterIndex * 17576 + secondLetterIndex * 676 + thirdLetterIndex * 26;

  const cover_index = (baseNumber % 24) + 1; // 1-24

  return `${API_HOST}/assets/covers/cover_${cover_index}.png`;
}

export const getId = (member: WorkspaceParticipant) =>
  'user' in member ? member.user : member.team;
